import React, { useState } from 'react';
import Markdown from 'react-markdown';
import { CalendarEvent, MeetingNote } from '../types';
import { updateCalendarEventDescription } from '../services/calendarService';

interface CalendarDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  events: CalendarEvent[];
  notes: MeetingNote[];
  accessToken: string | null;
  onAddNote?: (note: MeetingNote) => void;
  onUpdateNote?: (id: string, updates: Partial<MeetingNote>) => void;
}

const AttachNoteForm: React.FC<{
  event: CalendarEvent;
  availableNotes: MeetingNote[];
  accessToken: string | null;
  onAddNote: (note: MeetingNote) => void;
  onUpdateNote: (id: string, updates: Partial<MeetingNote>) => void;
  onCancel: () => void;
}> = ({ event, availableNotes, accessToken, onAddNote, onUpdateNote, onCancel }) => {
  const [mode, setMode] = useState<'write' | 'link'>('write');
  const [content, setContent] = useState('');
  const [selectedNoteId, setSelectedNoteId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleWriteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    setIsSaving(true);
    try {
      // If we have an access token, try to update the Google Calendar event description
      if (accessToken) {
        const newDescription = event.description 
          ? `${event.description}\n\n---\n**Meeting Notes:**\n${content}`
          : `**Meeting Notes:**\n${content}`;
          
        await updateCalendarEventDescription(accessToken, event.id, newDescription);
      }

      onAddNote({
        id: crypto.randomUUID(),
        title: event.title,
        date: new Date(event.startTime).toISOString().split('T')[0],
        content,
        source: 'manual',
        linkedEventIds: [event.id]
      });
      onCancel();
    } catch (error) {
      console.error("Failed to update calendar event:", error);
      alert("Failed to save note to calendar. The note will only be saved locally.");
      
      // Still save locally even if calendar update fails
      onAddNote({
        id: crypto.randomUUID(),
        title: event.title,
        date: new Date(event.startTime).toISOString().split('T')[0],
        content,
        source: 'manual',
        linkedEventIds: [event.id]
      });
      onCancel();
    } finally {
      setIsSaving(false);
    }
  };

  const handleLinkSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedNoteId) return;
    const note = availableNotes.find(n => n.id === selectedNoteId);
    if (note) {
      onUpdateNote(selectedNoteId, {
        linkedEventIds: [...(note.linkedEventIds || []), event.id]
      });
    }
    onCancel();
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200/60">
      <div className="flex space-x-2 mb-3">
        <button 
          onClick={() => setMode('write')}
          className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${mode === 'write' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-100'}`}
        >
          Write Note
        </button>
        <button 
          onClick={() => setMode('link')}
          className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${mode === 'link' ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-100'}`}
        >
          Link Existing
        </button>
      </div>

      {mode === 'write' ? (
        <form onSubmit={handleWriteSubmit}>
          <textarea 
            autoFocus
            rows={3}
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Jot down quick notes..."
            className="w-full text-xs p-2 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-500 outline-none resize-none mb-2"
            disabled={isSaving}
          />
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={onCancel} disabled={isSaving} className="text-xs text-slate-500 hover:text-slate-700 font-medium px-2 py-1">Cancel</button>
            <button type="submit" disabled={isSaving || !content.trim()} className="text-xs bg-indigo-600 text-white hover:bg-indigo-700 font-medium px-3 py-1 rounded-md disabled:opacity-50 flex items-center">
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : 'Save to Calendar'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleLinkSubmit}>
          <select 
            value={selectedNoteId}
            onChange={e => setSelectedNoteId(e.target.value)}
            className="w-full text-xs p-2 border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-500 outline-none mb-2"
          >
            <option value="">Select a note...</option>
            {availableNotes.map(n => (
              <option key={n.id} value={n.id}>{n.title} ({n.source})</option>
            ))}
          </select>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700 font-medium px-2 py-1">Cancel</button>
            <button type="submit" disabled={!selectedNoteId} className="text-xs bg-indigo-600 text-white hover:bg-indigo-700 font-medium px-3 py-1 rounded-md disabled:opacity-50">Link</button>
          </div>
        </form>
      )}
    </div>
  );
};

const CalendarDrawer: React.FC<CalendarDrawerProps> = ({ isOpen, onClose, events, notes, accessToken, onAddNote, onUpdateNote }) => {
  const [activeAttachEventId, setActiveAttachEventId] = useState<string | null>(null);

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = new Date(event.startTime).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {} as Record<string, CalendarEvent[]>);

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className={`fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      
      {/* Drawer panel */}
      <div className={`fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col border-l border-slate-200 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-900">Agenda & Notes</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {Object.keys(groupedEvents).length === 0 ? (
            <div className="text-center text-slate-500 mt-10">
              <p>No calendar events found.</p>
              <p className="text-sm mt-2">Sync your workspace to pull in events.</p>
            </div>
          ) : (
            Object.entries(groupedEvents).map(([date, dayEvents]) => (
              <div key={date} className="space-y-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider sticky top-0 bg-white py-1 z-10">
                  {date}
                </h3>
                <div className="space-y-3">
                  {dayEvents.map(event => {
                    // Try to find matching notes based on title or date
                    const eventDateStr = new Date(event.startTime).toISOString().split('T')[0];
                    const matchingNotes = notes.filter(n => {
                      if (n.linkedEventIds?.includes(event.id)) return true;

                      const noteDateStr = new Date(n.date).toISOString().split('T')[0];
                      const dateMatch = noteDateStr === eventDateStr;
                      
                      const cleanEventTitle = event.title.toLowerCase().trim();
                      const cleanNoteTitle = n.title.toLowerCase().trim();
                      
                      // 1. Exact or substring match of the whole title
                      if (cleanNoteTitle.includes(cleanEventTitle) || cleanEventTitle.includes(cleanNoteTitle)) {
                        return dateMatch;
                      }

                      // 2. Word overlap with stricter rules
                      const noiseWords = ['notes', 'meeting', 'sync', 'connect', 'call', 'discussion', 'update', 'catchup'];
                      
                      const getSignificantWords = (title: string) => {
                        return title.split(/[\s\W]+/)
                          .filter(w => w.length > 2) // allow 3-letter words
                          .filter(w => !noiseWords.includes(w));
                      };

                      const eventWords = getSignificantWords(cleanEventTitle);
                      const noteWords = getSignificantWords(cleanNoteTitle);
                      
                      if (eventWords.length === 0 || noteWords.length === 0) return false;

                      const commonWords = eventWords.filter(w => noteWords.includes(w));
                      
                      let titleMatch = false;
                      
                      // If the event title is very short (1-2 significant words), ALL of them must be in the note title.
                      if (eventWords.length <= 2) {
                        titleMatch = commonWords.length === eventWords.length;
                      } else {
                        // Otherwise, require at least 60% overlap of the event's significant words
                        titleMatch = (commonWords.length / eventWords.length) >= 0.6;
                      }

                      return dateMatch && titleMatch;
                    });

                    const unlinkedNotes = notes.filter(n => !matchingNotes.some(mn => mn.id === n.id));

                    return (
                      <div key={event.id} className="bg-slate-50 border border-slate-100 rounded-xl p-4 hover:border-indigo-200 transition-colors group">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-slate-800 text-sm">{event.title}</h4>
                          <span className="text-xs font-medium text-slate-500 whitespace-nowrap ml-2">
                            {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        
                        {event.attendees && event.attendees.length > 0 && (
                          <div className="flex items-center text-xs text-slate-500 mb-3">
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                            <span className="truncate">{event.attendees.length} attendees</span>
                          </div>
                        )}

                        {matchingNotes.length > 0 ? (
                          <div className="mt-3 pt-3 border-t border-slate-200/60 space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-semibold text-indigo-600 flex items-center">
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {matchingNotes.length} Linked Note{matchingNotes.length > 1 ? 's' : ''}
                              </p>
                              {!activeAttachEventId && onAddNote && onUpdateNote && (
                                <button 
                                  onClick={() => setActiveAttachEventId(event.id)}
                                  className="text-[10px] font-medium text-slate-400 hover:text-indigo-600 transition-colors"
                                >
                                  + Add More
                                </button>
                              )}
                            </div>
                            {matchingNotes.map(note => {
                              const url = note.url;

                              return (
                                <div 
                                  key={note.id} 
                                  onClick={() => { if (url) window.open(url, '_blank'); }}
                                  className={`block bg-white p-2.5 rounded-lg border border-slate-200 shadow-sm transition-all ${url ? 'hover:border-indigo-300 hover:shadow-md cursor-pointer' : ''}`}
                                >
                                  <div className="font-semibold text-slate-800 text-xs mb-1 truncate flex items-center">
                                    {note.source === 'gmail' && <span className="mr-1.5">📧</span>}
                                    {note.source === 'gdoc' && <span className="mr-1.5">📄</span>}
                                    {note.source === 'gsheet' && <span className="mr-1.5">📊</span>}
                                    {note.source === 'manual' && <span className="mr-1.5">✍️</span>}
                                    <span className="truncate">{note.title}</span>
                                  </div>
                                  <div className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                                    <Markdown
                                      components={{
                                        a: ({node, ...props}) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium" onClick={(e) => e.stopPropagation()} />,
                                        p: ({node, ...props}) => <span {...props} className="mr-1" />
                                      }}
                                    >
                                      {note.content}
                                    </Markdown>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          !activeAttachEventId && onAddNote && onUpdateNote && (
                            <div className="mt-3 pt-3 border-t border-slate-200/60">
                              <button 
                                onClick={() => setActiveAttachEventId(event.id)}
                                className="text-xs font-medium text-slate-400 hover:text-indigo-600 flex items-center transition-colors"
                              >
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Attach Notes
                              </button>
                            </div>
                          )
                        )}

                        {activeAttachEventId === event.id && onAddNote && onUpdateNote && (
                          <AttachNoteForm 
                            event={event}
                            availableNotes={unlinkedNotes}
                            accessToken={accessToken}
                            onAddNote={onAddNote}
                            onUpdateNote={onUpdateNote}
                            onCancel={() => setActiveAttachEventId(null)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default CalendarDrawer;
